import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Loader2, Trash2, X } from 'lucide-react';
import { CompetitorScreen, DetailPagePlannerData } from '@/types';
import { renderFormattedMessage } from '@/components/agent/components/formatMarkdown';
import { Lightbox } from '@/components/agent/components/Lightbox';
import { countArchitectureBlocks } from '../../hooks/useDetailPageState';

/** 品牌色（与 DetailPagePipelineView 同源） */
const ACCENT = '#6D5BF5';

interface PlannerResultPanelProps {
  open: boolean;
  onClose: () => void;
  planner: DetailPagePlannerData;
  nodeTitle: string;
  /** planner 会话 style_confirmation 挂起时非空：底部显示「确认方案」条件按钮（组件纯展示，提交由 onConfirm 承接） */
  pendingConfirmation?: { currentStep: string } | null;
  onConfirm?: () => void;
  /** 竞品屏切块删除回调（P2.5 决策点 3：识别有误的坏块可删），缺省时切块不可删 */
  onDeleteScreen?: (index: number) => void;
}

type TabKey = 'product' | 'competitor' | 'copywriting' | 'architecture';

const TAB_DEFS: Array<{ key: TabKey; label: string }> = [
  { key: 'product', label: '产品识别' },
  { key: 'competitor', label: '竞品分析' },
  { key: 'copywriting', label: '策划文案' },
  { key: 'architecture', label: '详情页架构' },
];

/** 策划架构查看面板（§4.4）：全屏遮罩 + 4 Tab 标头切换，只读渲染 planner 四切片 */
export const PlannerResultPanel: React.FC<PlannerResultPanelProps> = ({
  open,
  onClose,
  planner,
  pendingConfirmation,
  onConfirm,
  onDeleteScreen,
}) => {
  const [activeTab, setActiveTab] = useState<TabKey>('architecture');
  const [confirming, setConfirming] = useState(false);
  // 竞品屏切块大图查看（P2.5）：非空时在面板内浮层展示整屏原图
  const [lightboxScreen, setLightboxScreen] = useState<CompetitorScreen | null>(null);

  // 每次打开重置到「详情页架构」Tab（与架构策划条目语义衔接：用户首先看到架构表），并复位确认按钮提交态
  useEffect(() => {
    if (open) {
      setActiveTab('architecture');
      setConfirming(false);
      setLightboxScreen(null);
    }
  }, [open]);

  // Esc 关闭；点击背景不关闭（防误触丢失阅读位置）。
  // 满屏面板期间在 capture 阶段处理画布全局快捷键（全局钩子 useKeyboardShortcuts 挂 window bubble
  // 且不感知面板：无条件 Ctrl+A 全选节点、有选中时 Ctrl+C 复制节点，不阻断会在用户不可见时操作画布）。
  // Ctrl+A / Ctrl+C 放行浏览器默认（全选/复制面板文字），仅 stopImmediatePropagation 阻断画布钩子；
  // 不能用面板 DOM contains(e.target) 判定「按键发生在面板内」——面板文字不可聚焦，keydown 的
  // target 恒为 body，contains 判断永远为 false。
  // 其余快捷键（Ctrl+Z 撤销 / Ctrl+Y 重做 / Ctrl+V 粘贴 / Ctrl+D 原地复制 / Delete/Backspace 删除）
  // 在面板内无合理默认行为，preventDefault + 阻断，防止面板打开期间回滚/删除画布
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // 灯箱打开时 Esc 只关灯箱，再按才关面板
        if (lightboxScreen) {
          setLightboxScreen(null);
          return;
        }
        onClose();
        return;
      }
      const ctrl = e.ctrlKey || e.metaKey;
      const key = e.key.toLowerCase();
      if (ctrl && (key === 'a' || key === 'c')) {
        e.stopImmediatePropagation();
        return;
      }
      if (
        e.key === 'Delete' ||
        e.key === 'Backspace' ||
        (ctrl && ['z', 'y', 'v', 'd'].includes(key))
      ) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [open, onClose, lightboxScreen]);

  const markdownByTab: Record<TabKey, string> = {
    product: planner.productAnalysis || '',
    competitor: planner.competitorAnalysis || '',
    copywriting: planner.copywritingResult || '',
    architecture: planner.architectureTable || '',
  };
  const markdown = markdownByTab[activeTab];
  const blockCount = countArchitectureBlocks(planner.architectureTable);
  const showConfirm = !!pendingConfirmation && pendingConfirmation.currentStep === 'style_confirmation';
  // 竞品屏切块（P2.5）仅竞品页签展示：[] = 切屏落库进行中，undefined = 未切屏（整图模式，不渲染本区）
  const screens: CompetitorScreen[] | undefined =
    activeTab === 'competitor' ? planner.competitorScreens : undefined;

  const rendered = useMemo(
    () => (markdown.trim() ? renderFormattedMessage(markdown, false, 'lg') : null),
    [markdown],
  );

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[999] bg-[#1c1c1e] overflow-hidden"
      style={{ animation: 'planner-panel-in 200ms ease-out' }}
    >
      {/* 内容层：动画（含 transform）留在外层根元素，避免 transform 属性冲突 */}
      <div className="flex h-full w-full flex-col">
      <style>{`@keyframes planner-panel-in { from { opacity: 0; transform: scale(0.97); } to { opacity: 1; transform: scale(1); } }
        /* 架构表紧凑样式（仅 planner-table-scope 作用域，参考合成提示词全屏表格）：
           外轮廓框+圆角、内部横竖分隔线、13px 小字号、窄行高；不影响 agent 会话内同渲染器的表格 */
        .planner-table-scope .overflow-x-auto { margin: 0; border: 1px solid rgba(255,255,255,0.14); border-radius: 12px; box-shadow: none; }
        .planner-table-scope table { font-size: 13px; }
        .planner-table-scope table th { padding: 12px 14px; color: rgba(255,255,255,0.55); font-weight: 500; background: rgba(255,255,255,0.04); border-bottom: 1px solid rgba(255,255,255,0.12); border-right: 1px solid rgba(255,255,255,0.08); }
        .planner-table-scope table th:last-child { border-right: none; }
        .planner-table-scope table td { padding: 12px 14px; color: rgba(255,255,255,0.78); line-height: 1.7; border-bottom: 1px solid rgba(255,255,255,0.07); border-right: 1px solid rgba(255,255,255,0.06); }
        .planner-table-scope table td:last-child { border-right: none; }
        .planner-table-scope table tbody tr:last-child td { border-bottom: none; }
      `}</style>

      {/* 单排顶栏：无标题，4 页签全宽水平居中（垂直中心对齐）；关闭按钮绝对定位居右，不挤占页签居中 */}
      <div className="relative shrink-0 h-[68px] flex items-stretch justify-center border-b border-white/[0.08]">
        <div className="flex items-stretch justify-center gap-10">
          {TAB_DEFS.map((tab, i) => {
            const active = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`relative flex items-center gap-2 text-[19px] transition-colors duration-200 shrink-0 ${
                  active ? 'text-white' : 'text-white/45 hover:text-white/75'
                }`}
              >
                <span
                  className="text-[14px] font-medium tabular-nums text-white/30 transition-colors duration-200"
                  style={active ? { color: ACCENT } : undefined}
                >
                  {String(i + 1).padStart(2, '0')}
                </span>
                {tab.label}
                {tab.key === 'architecture' && blockCount > 0 && (
                  <span className="px-2 h-6 rounded-full bg-white/[0.08] text-[13px] text-white/55 flex items-center">
                    {blockCount} 区块
                  </span>
                )}
                {active && (
                  <span
                    className="absolute left-0 right-0 -bottom-px h-[2px] rounded-full"
                    style={{ background: ACCENT }}
                  />
                )}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="absolute right-6 top-1/2 -translate-y-1/2 shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/[0.08] transition-colors"
        >
          <X size={22} />
        </button>
      </div>

      {/* key 按 Tab 重挂载：切换页签时滚动位置归顶，避免长文档间切换落在新内容中部。
          架构页签全宽铺开呈现表格（参考合成提示词界面），文案类页签保持限宽阅读度量 */}
      <div key={activeTab} className="flex-1 min-h-0 overflow-y-auto px-8 pt-6 pb-10">
        {screens !== undefined && (
          <div className="max-w-[1080px] mx-auto mb-6">
            {screens.length === 0 ? (
              <div className="h-16 rounded-xl border border-white/[0.08] bg-white/[0.03] flex items-center justify-center gap-2.5 text-[15px] text-white/50">
                <Loader2 size={16} className="animate-spin" />
                竞品长图切屏分析中，完成后按屏归档模块类型与版式要点
              </div>
            ) : (
              <>
                <div className="mb-3 text-[15px] text-white/55">
                  竞品长图已切为 {screens.length} 屏，拆分出图时按区块模块类型自动匹配参考屏
                  {onDeleteScreen ? '，识别有误的屏可删除' : ''}，点击图片可放大查看
                </div>
                <div className="grid grid-cols-3 gap-4">
                  {screens.map((screen) => (
                    <div
                      key={screen.index}
                      className="relative group rounded-xl overflow-hidden border border-white/[0.1] bg-white/[0.03]"
                    >
                      <img
                        src={screen.url}
                        alt={`竞品屏 ${screen.index}`}
                        className="w-full aspect-[9/16] object-cover object-top cursor-zoom-in"
                        draggable={false}
                        onClick={() => setLightboxScreen(screen)}
                      />
                      {onDeleteScreen && (
                        <button
                          type="button"
                          onClick={() => onDeleteScreen(screen.index)}
                          title="删除该屏（删除后出图参考按剩余屏匹配）"
                          className="absolute top-2 right-2 w-8 h-8 rounded-lg bg-black/60 backdrop-blur flex items-center justify-center text-white/70 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 size={15} />
                        </button>
                      )}
                      <div className="px-3 py-2.5">
                        <div className="flex items-center gap-2 min-w-0">
                          <span
                            className="px-2 h-6 shrink-0 rounded-full text-[12px] font-medium flex items-center"
                            style={{ background: `${ACCENT}26`, color: ACCENT }}
                          >
                            {screen.moduleType}
                          </span>
                          <span className="text-[12px] text-white/35 tabular-nums truncate">
                            第 {screen.index} 屏 · 占原图 {Math.round(screen.heightPct)}%
                          </span>
                        </div>
                        {screen.styleNotes && (
                          <p className="mt-1.5 text-[13px] leading-relaxed text-white/55">
                            {screen.styleNotes}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
        {rendered ? (
          <div
            className={
              activeTab === 'architecture'
                ? 'w-full planner-table-scope text-white/85'
                : 'max-w-[1080px] mx-auto text-white/85'
            }
          >
            {rendered}
          </div>
        ) : screens !== undefined ? null : (
          <div className="h-full min-h-[320px] flex flex-col items-center justify-center gap-2">
            <span className="text-[24px] text-white/60">
              {activeTab === 'competitor'
                ? planner.skipCompetitor
                  ? '已跳过竞品分析'
                  : '暂无竞品分析内容'
                : '暂无内容'}
            </span>
            {activeTab === 'competitor' && (
              <span className="text-[20px] text-white/35">
                {planner.skipCompetitor ? '该节点未接入竞品参考图' : '竞品分析未产出结果，可重新生成'}
              </span>
            )}
          </div>
        )}
      </div>

      {showConfirm && onConfirm && (
        <div className="shrink-0 px-10 py-5 border-t border-white/[0.08] flex justify-end">
          <button
            type="button"
            onClick={() => {
              if (confirming) return;
              setConfirming(true);
              onConfirm();
            }}
            disabled={confirming}
            className={`h-14 px-8 rounded-xl text-[20px] font-medium text-white flex items-center gap-2 transition-all hover:brightness-110 ${confirming ? 'opacity-60 cursor-not-allowed' : ''}`}
            style={{ background: ACCENT, boxShadow: `0 8px 24px -8px ${ACCENT}66` }}
          >
            <Check size={20} />
            确认方案，生成分屏提示词
          </button>
        </div>
      )}

      {/* 竞品屏大图查看：浮层位于面板 stacking context 内（z-50 盖过面板内容），Esc 分层关闭 */}
      {lightboxScreen && (
        <Lightbox
          src={lightboxScreen.url}
          title={`竞品屏 ${lightboxScreen.index} · ${lightboxScreen.moduleType}`}
          onClose={() => setLightboxScreen(null)}
        />
      )}
      </div>
    </div>,
    document.body,
  );
};
